import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';

import { cn } from '@/lib/utils';

interface CountdownValues {
	days: number;
	hours: number;
	minutes: number;
	seconds: number;
	expired: boolean;
}

function diff(target: Date): CountdownValues {
	const ms = target.getTime() - Date.now();
	if (ms <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
	const s = Math.floor(ms / 1000);
	return {
		days: Math.floor(s / 86400),
		hours: Math.floor((s % 86400) / 3600),
		minutes: Math.floor((s % 3600) / 60),
		seconds: s % 60,
		expired: false,
	};
}

export function useCountdown(targetIso: string | null | undefined): CountdownValues {
	const [value, setValue] = useState<CountdownValues>(() =>
		targetIso
			? diff(new Date(targetIso))
			: { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true },
	);
	useEffect(() => {
		if (!targetIso) return;
		const target = new Date(targetIso);
		setValue(diff(target));
		const id = setInterval(() => setValue(diff(target)), 1000);
		return () => clearInterval(id);
	}, [targetIso]);
	return value;
}

interface CountdownProps {
	targetIso: string | null | undefined;
	compact?: boolean;
	className?: string;
}

function pad(n: number): string {
	return String(n).padStart(2, '0');
}

function Unit({ value, label }: { value: number; label: string }) {
	return (
		<div className="flex min-w-[68px] flex-col items-center rounded-[10px] border border-loomlite-border-strong bg-loomlite-ink/60 px-3.5 py-2.5">
			<span className="font-heading text-[28px] font-bold leading-none text-loomlite-text tabular-nums">
				{pad(value)}
			</span>
			<span className="mt-1 text-[10px] uppercase tracking-[0.1em] text-loomlite-dim">{label}</span>
		</div>
	);
}

export function Countdown({ targetIso, compact, className }: CountdownProps) {
	const v = useCountdown(targetIso);
	if (compact) {
		return (
			<span
				className={cn(
					'inline-flex items-center gap-1.5 font-heading text-[13px] font-semibold text-loomlite-gold-soft tabular-nums',
					className,
				)}
			>
				<Clock className="h-3.5 w-3.5" />
				{v.expired ? 'closed' : `${v.days}d ${pad(v.hours)}:${pad(v.minutes)}:${pad(v.seconds)}`}
			</span>
		);
	}
	return (
		<div className={cn('flex gap-2', className)}>
			<Unit value={v.days} label="Days" />
			<Unit value={v.hours} label="Hours" />
			<Unit value={v.minutes} label="Minutes" />
			<Unit value={v.seconds} label="Seconds" />
		</div>
	);
}
